import { useEffect, useRef, useState, useCallback } from "react";
import axios from "../../../config/axios";
import useBeaconControl from "../../../hooks/useBeaconControl";

// ---- Alarm sound (loops until stopped) ----
const ALARM_SRC = "/static/timer-alarm.mp3";
const ALARM_VOLUME = 0.7;
let alarmAudio = null;
const startAlarm = () => {
  try {
    if (!alarmAudio) { alarmAudio = new Audio(ALARM_SRC); alarmAudio.loop = true; }
    alarmAudio.volume = ALARM_VOLUME;
    alarmAudio.currentTime = 0;
    alarmAudio.play().catch((e) => console.log("[Timer] alarm blocked:", e?.name));
  } catch (e) { console.log("[Timer] alarm failed:", e); }
};
const stopAlarm = () => {
  try { if (alarmAudio) { alarmAudio.pause(); alarmAudio.currentTime = 0; } } catch {}
};

// ---- Presets (seconds) ----
const PRESETS = [
  { label: "30 SEC", secs: 30 },
  { label: "1 MIN",  secs: 60 },
  { label: "5 MIN",  secs: 300 },
  { label: "10 MIN", secs: 600 },
  { label: "30 MIN", secs: 1800 },
  { label: "60 MIN", secs: 3600 },
];
const MIN_SECS = 30; // compact pre-roll fits; 30s floor keeps it clean

// ---- Band schedules, seconds relative to T=0 (negative = before zero).
// Tail (both schedules): CIRCLE_RED x2 3s apart, 4s run, CANCEL, 2s, one
// keep-awake WAKE_UP_FAST, 3s, CANCEL, 2s, LIGHTNING_RED double-hit landing
// on 0:00 (proven 1.5s band lead). After T+1.5 the ALARM phase owns the
// bands (repeats + wind-down) — schedules end there on purpose.
// FULL adds the long warmup (SLOW 5s + five FASTs); COMPACT fits 30s timers.
const BAND_EVENTS_FULL = [
  { at: -49,   fx: "CANCEL" },
  { at: -47,   fx: "WAKE_UP_SLOW", vib: "OFF" },
  { at: -42,   fx: "CANCEL" },
  { at: -40,   fx: "WAKE_UP_FAST", vib: "OFF" },
  { at: -35.5, fx: "WAKE_UP_FAST", vib: "OFF" },
  { at: -31,   fx: "WAKE_UP_FAST", vib: "OFF" },
  { at: -26.5, fx: "WAKE_UP_FAST", vib: "OFF" },
  { at: -22,   fx: "WAKE_UP_FAST", vib: "OFF" },
  { at: -17.5, fx: "CANCEL" },
  { at: -15.5, fx: "CIRCLE_RED", vib: "OFF" },
  { at: -12.5, fx: "CIRCLE_RED", vib: "OFF" },
  { at: -8.5,  fx: "CANCEL" },
  { at: -6.5,  fx: "WAKE_UP_FAST", vib: "OFF" },
  { at: -3.5,  fx: "CANCEL" },
  { at: -1.5,  fx: "LIGHTNING_RED", vib: "PULSE" },
  { at: 1.5,   fx: "LIGHTNING_RED", vib: "PULSE" },
];
const BAND_EVENTS_COMPACT = [
  { at: -27,   fx: "CANCEL" },
  { at: -25,   fx: "WAKE_UP_SLOW", vib: "OFF" },
  { at: -21,   fx: "CANCEL" },
  { at: -19.5, fx: "WAKE_UP_FAST", vib: "OFF" },
  { at: -17.5, fx: "CANCEL" },
  { at: -15.5, fx: "CIRCLE_RED", vib: "OFF" },
  { at: -12.5, fx: "CIRCLE_RED", vib: "OFF" },
  { at: -8.5,  fx: "CANCEL" },
  { at: -6.5,  fx: "WAKE_UP_FAST", vib: "OFF" },
  { at: -3.5,  fx: "CANCEL" },
  { at: -1.5,  fx: "LIGHTNING_RED", vib: "PULSE" },
  { at: 1.5,   fx: "LIGHTNING_RED", vib: "PULSE" },
];
const FULL_SCHEDULE_MIN_SECS = 55; // >= this gets the long warmup (49s pre-roll)
// Alarm phase: after the T+1.5 hit, LIGHTNING_RED repeats every 5s while the
// alarm rings, capped at 60s past zero. Stop (or the cap) winds down with
// 2s → CANCEL → 2s → SOLID_OFF, timed from the LAST lightning so the CANCEL
// always lands after it — never buried under a fresh burst.
const ALARM_REPEAT_MS = 5000;
const ALARM_MAX_MS = 60000;

const GAP_MS = 2000; // same 2s command-gap rule as the scanner

/* ---- Raw transport — same endpoints as Interact/BeaconDemo ---- */
const postShowCmd = (deviceId, fx, vib) =>
  axios.post("/esp/beacon-show", { deviceId, fx, vib }, { withCredentials: true })
    .catch((err) => console.error("[Timer] cmd failed:", fx, err));
const postCancelRaw = (deviceId) =>
  axios.post("/esp/cancel", { deviceId }, { withCredentials: true })
    .catch((err) => console.error("[Timer] cancel failed:", err));

const pad2 = (n) => String(n).padStart(2, "0");
const fmt = (secs) => {
  const s = Math.max(0, Math.ceil(secs));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  return h > 0 ? `${h}:${pad2(m)}:${pad2(r)}` : `${pad2(m)}:${pad2(r)}`;
};

const IXT_STYLE = `
  .mb-strips{position:relative;height:0;z-index:500;overflow:visible;}
  .mb-strips > div{box-shadow:0 4px 14px rgba(20,30,60,.25);}
  .ixt-timer{display:grid;grid-template-columns:1fr 1.1fr;gap:24px;width:100%;height:100%;
    min-height:0;font-family:"Lato",sans-serif;color:#1a2542;position:relative;}
  .ixt-panel{background:#fff;border-radius:26px;padding:22px 26px;display:flex;
    flex-direction:column;box-shadow:0 10px 34px rgba(20,30,60,.08);min-height:0;overflow:hidden;}
  .ixt-tag{align-self:flex-start;background:#007bff;color:#fff;font-size:14px;
    font-weight:900;letter-spacing:2px;padding:5px 12px;border-radius:6px;margin-bottom:8px;}
  .ixt-title{font-size:28px;font-weight:900;margin:0 0 4px;letter-spacing:-.5px;line-height:1.05;}
  .ixt-tagline{font-size:16px;font-weight:800;color:#007bff;margin:0 0 12px;}
  .ixt-presets{display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;margin-bottom:12px;}
  .ixt-preset{height:46px;border-radius:14px;border:3px solid #007bff;background:#fff;
    font-family:"Lato",sans-serif;font-size:16px;font-weight:900;letter-spacing:.5px;color:#1a2542;
    cursor:pointer;outline:none;display:flex;align-items:center;justify-content:center;
    transition:transform .1s ease, background .12s ease, color .12s ease;}
  .ixt-preset:active{transform:scale(.96);}
  .ixt-preset.on{background:#007bff;color:#fff;}
  .ixt-custom{background:#f1f3f7;border:none;border-radius:14px;
    padding:10px 12px 8px;margin-bottom:12px;}
  .ixt-custom-label{font-size:12px;font-weight:900;letter-spacing:2px;color:#46506b;
    text-align:center;margin-bottom:6px;}
  .ixt-manual{display:flex;gap:12px;justify-content:center;align-items:flex-start;}
  .ixt-unit{display:flex;flex-direction:column;align-items:center;gap:4px;}
  .ixt-unit-label{font-size:11px;font-weight:900;letter-spacing:2px;color:#46506b;}
  .ixt-step{width:54px;height:36px;border-radius:10px;border:3px solid #d8dce3;background:#fff;
    font-family:"Lato",sans-serif;font-size:22px;font-weight:900;color:#1a2542;cursor:pointer;
    outline:none;line-height:1;}
  .ixt-step:active{background:#eef2f8;}
  .ixt-unit-val{font-size:30px;font-weight:900;min-width:58px;text-align:center;
    font-variant-numeric:tabular-nums;}
  .ixt-actions{margin-top:auto;display:flex;gap:12px;}
  .ixt-btn{flex:1;height:56px;border-radius:14px;border:3px solid transparent;
    font-family:"Lato",sans-serif;font-size:20px;font-weight:900;letter-spacing:1px;cursor:pointer;
    outline:none;color:#fff;display:flex;align-items:center;justify-content:center;
    transition:transform .1s ease, background .12s ease;}
  .ixt-btn:active{transform:scale(.97);}
  .ixt-start{background:#007bff;text-transform:uppercase;letter-spacing:2px;}
  .ixt-start:active{background:#0066d6;}
  .ixt-start:disabled{background:#b9c0cd;cursor:default;}
  .ixt-stop{background:#e5484d;text-transform:uppercase;letter-spacing:2px;}
  .ixt-stop:active{background:#c73737;}
  .ixt-back{background:#fff;color:#1a2542;border-color:#d8dce3;flex:0 0 120px;}
  .ixt-note{margin-top:8px;font-size:11px;color:#8a93a6;text-align:center;}
  .ixt-clockpanel{align-items:center;justify-content:center;gap:10px;transition:background .3s ease;}
  .ixt-ringwrap{position:relative;width:300px;height:300px;display:flex;
    align-items:center;justify-content:center;}
  .ixt-ring-svg{position:absolute;inset:0;transform:rotate(-90deg);}
  .ixt-ring-track{fill:none;stroke:#eef1f7;stroke-width:14;}
  .ixt-ring-bar{fill:none;stroke-width:14;stroke-linecap:round;
    transition:stroke-dashoffset .25s linear, stroke .4s ease;}
  .ixt-clock{font-weight:900;letter-spacing:1px;color:#1a2542;line-height:1;
    font-variant-numeric:tabular-nums;transition:color .4s ease;position:relative;z-index:1;}
  .ixt-clock.amber{color:#ff9f0a;text-shadow:0 0 22px rgba(255,159,10,.4);}
  .ixt-clock.red{color:#ff453a;text-shadow:0 0 26px rgba(255,69,58,.5);
    animation:ixt-pulse 1s ease-in-out infinite;}
  .ixt-sub{font-size:18px;font-weight:900;color:#8a93a6;letter-spacing:2px;}
  .ixt-alarm.ixt-clockpanel,
  .ixt-alarm .ixt-clockpanel{cursor:pointer;}
  .ixt-timer.ixt-alarming .ixt-clockpanel{animation:ixt-alarm-bg .8s linear infinite;}
  .ixt-timer.ixt-alarming .ixt-clock{color:#fff;animation:none;text-shadow:none;}
  .ixt-timer.ixt-alarming .ixt-sub{color:#fff;}
  .ixt-timer.ixt-alarming .ixt-ring-track,
  .ixt-timer.ixt-alarming .ixt-ring-bar{stroke:rgba(255,255,255,.35);}
  @keyframes ixt-pulse{0%,100%{transform:scale(1);}50%{transform:scale(1.05);}}
  @keyframes ixt-alarm-bg{0%,49%{background:#e5484d;}50%,100%{background:#a61b20;}}
`;

const RING_R = 132;
const RING_C = 2 * Math.PI * RING_R;

const InteractTimer = ({ onBack, device }) => {
  const [duration, setDuration] = useState(300);
  const [preset, setPreset] = useState(300);
  const [manH, setManH] = useState(0);
  const [manM, setManM] = useState(5);
  const [manS, setManS] = useState(0);
  const [running, setRunning] = useState(false);
  const [alarm, setAlarm] = useState(false);
  const [remaining, setRemaining] = useState(300);

  /* ---- Beacon control + gated senders (Interact pattern) ---- */
  const { isController, hasHolder, holder, claim } = useBeaconControl();
  const [pendingControl, setPendingControl] = useState(false); // take-control modal before Start
  const isControllerRef = useRef(isController);
  useEffect(() => { isControllerRef.current = isController; }, [isController]);
  const deviceId = device?.magic_beacon_device?._id ?? null;
  const deviceIdRef = useRef(deviceId);
  useEffect(() => { deviceIdRef.current = deviceId; }, [deviceId]);
  const lastCmdAtRef = useRef(0);
  const sentAnyRef = useRef(false); // any real command left this session? (no → no wind-down needed)
  const canSend = useCallback(() => !!deviceIdRef.current && !!isControllerRef.current, []);
  const sendCmd = useCallback((fx, vib) => {
    if (!canSend()) { console.log(`[Timer] SKIPPED fx=${fx}`); return Promise.resolve(); }
    console.log(`[Timer] send fx=${fx} vib=${vib}`);
    sentAnyRef.current = true;
    lastCmdAtRef.current = Date.now();
    return postShowCmd(deviceIdRef.current, fx, vib);
  }, [canSend]);
  const sendCancel = useCallback(() => {
    if (!canSend()) { console.log("[Timer] SKIPPED CANCEL"); return Promise.resolve(); }
    console.log("[Timer] send CANCEL");
    sentAnyRef.current = true;
    lastCmdAtRef.current = Date.now();
    return postCancelRaw(deviceIdRef.current);
  }, [canSend]);

  /* ---- Engine: wall-clock end time, 250ms tick, one-shot band events ---- */
  const endAtRef = useRef(0);
  const tickRef = useRef(null);
  const firedRef = useRef(new Set());
  const scheduleRef = useRef(BAND_EVENTS_FULL);
  const stopTimersRef = useRef([]);
  const alarmRepeatRef = useRef(null);
  const stopRef = useRef(null); // tick's interval closes over stale stop; ref stays fresh

  const clearEngine = useCallback(() => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (alarmRepeatRef.current) { clearInterval(alarmRepeatRef.current); alarmRepeatRef.current = null; }
    stopTimersRef.current.forEach(clearTimeout);
    stopTimersRef.current = [];
  }, []);

  const tick = useCallback(() => {
    const remainSecs = (endAtRef.current - Date.now()) / 1000;
    setRemaining(remainSecs);
    scheduleRef.current.forEach((ev, i) => {
      if (firedRef.current.has(i)) return;
      if (remainSecs <= -ev.at) {
        firedRef.current.add(i);
        ev.fx === "CANCEL" ? sendCancel() : sendCmd(ev.fx, ev.vib);
      }
    });
    if (remainSecs <= 0 && !firedRef.current.has("alarm")) {
      firedRef.current.add("alarm");
      setAlarm(true);
      startAlarm();
      // Lightning repeats every 5s (first repeat 5s after the T+1.5 hit),
      // capped at 60s past zero, then auto wind-down as if stopped.
      alarmRepeatRef.current = setInterval(() => {
        if (Date.now() - endAtRef.current >= ALARM_MAX_MS) {
          stopRef.current?.();
          return;
        }
        sendCmd("LIGHTNING_RED", "PULSE");
      }, ALARM_REPEAT_MS);
    }
  }, [sendCancel, sendCmd]);

  const manualSecs = manH * 3600 + manM * 60 + manS;
  const chosenSecs = preset != null ? preset : manualSecs;

  const start = () => {
    if (running) return;
    const secs = Math.max(MIN_SECS, chosenSecs);
    setDuration(secs);
    setRemaining(secs);
    firedRef.current = new Set();
    scheduleRef.current = secs >= FULL_SCHEDULE_MIN_SECS ? BAND_EVENTS_FULL : BAND_EVENTS_COMPACT;
    endAtRef.current = Date.now() + secs * 1000;
    setRunning(true);
    setAlarm(false);
    tickRef.current = setInterval(tick, 250);
  };

  // Start is gated on control: without it every band command gets SKIPPED and
  // the timer would run lightless without warning. Same claim/modal pattern
  // as Interact. Saying No still starts — plain timer, no lights.
  const requestStart = async () => {
    if (running) return;
    if (isController) { start(); return; }
    if (hasHolder && holder?.holderDeviceId !== device?._id) {
      setPendingControl(true);
      return;
    }
    const r = await claim();
    if (r?.ok) start();
    else { console.error("[Timer] claim failed:", r?.reason); start(); }
  };
  const confirmTakeControl = async () => {
    setPendingControl(false);
    const r = await claim();
    if (!r?.ok) console.error("[Timer] take-control claim failed:", r?.reason);
    start(); // start either way; without control it runs lightless
  };
  // No = leave control where it is, but run anyway as a plain timer
  // (band commands SKIP without control — screen + alarm still work).
  const cancelTakeControl = () => { setPendingControl(false); start(); };

  // Stop / alarm dismissal / 60s alarm cap. Bands only get the wind-down if
  // any command went out this run — stopping with 6 minutes left is silent.
  // The CANCEL waits out the 2s gap from the LAST command (incl. the last
  // repeat lightning), so it always lands after the final burst.
  const stop = useCallback(() => {
    stopAlarm();
    clearEngine();
    setRunning(false);
    setAlarm(false);
    if (sentAnyRef.current) {
      sentAnyRef.current = false;
      const wait = Math.max(0, lastCmdAtRef.current + GAP_MS - Date.now());
      const t1 = setTimeout(() => {
        sendCancel();
        const t2 = setTimeout(() => sendCmd("SOLID_OFF", "OFF"), GAP_MS);
        stopTimersRef.current.push(t2);
      }, wait);
      stopTimersRef.current.push(t1);
      sentAnyRef.current = false;
    }
    setRemaining(Math.max(MIN_SECS, chosenSecs));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearEngine, sendCancel, sendCmd, chosenSecs]);
  useEffect(() => { stopRef.current = stop; }, [stop]);

  // Unmount (navigated away): alarm off, engine dead, CANCEL only if bands
  // were touched. The timer does NOT keep running behind other pages.
  useEffect(() => () => {
    stopAlarm();
    clearEngine();
    if (sentAnyRef.current) sendCancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const belowMin = preset == null && manualSecs < MIN_SECS;
  const inLast30 = running && !alarm && remaining <= 30;
  const inLast10 = running && !alarm && remaining <= 10;

  // Manual bump. Increasing SEC with no hours/minutes floors at 30 —
  // that's the minimum the timer will run anyway.
  const bumpUnit = (u, dir) => {
    setPreset(null);
    u.set((v) => {
      let n = v + dir;
      if (n < 0) n = u.max;
      if (n > u.max) n = 0;
      if (u.label === "SEC" && dir > 0 && manH === 0 && manM === 0 && n < 30) n = 30;
      return n;
    });
  };

  // Preset picked → manual mirrors it, so "1 MIN then +4 sec" just works.
  const pickPreset = (secs) => {
    if (running) return;
    setPreset(secs);
    setRemaining(secs);
    setManH(Math.floor(secs / 3600));
    setManM(Math.floor((secs % 3600) / 60));
    setManS(secs % 60);
  };

  const clockStr = alarm ? "0:00" : fmt(running ? remaining : Math.max(MIN_SECS, chosenSecs));
  const clockSize = clockStr.length > 5 ? 52 : 68;
  const frac = running && duration > 0 ? Math.max(0, Math.min(1, remaining / duration)) : 1;
  const ringStroke = inLast10 ? "#ff453a" : inLast30 ? "#ff9f0a" : "url(#ixt-ring-grad)";

  return (
    <div className={`ixt-timer ${alarm ? "ixt-alarming" : ""}`}>
      <style>{IXT_STYLE}</style>

      {/* Left: setup */}
      <div className="ixt-panel">
        <div className="ixt-tag">TIMER</div>
        <h1 className="ixt-title">Set A Timer</h1>
        <div className="ixt-tagline">Your bands will flash &amp; buzz when time is up!</div>
        <div className="ixt-presets">
          {PRESETS.map((p) => (
            <button
              key={p.secs}
              className={`ixt-preset ${preset === p.secs ? "on" : ""}`}
              onClick={() => pickPreset(p.secs)}
              disabled={running}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="ixt-custom">
          <div className="ixt-custom-label">CUSTOM</div>
          <div className="ixt-manual">
            {[
              { label: "HOURS", val: manH, set: setManH, max: 9 },
              { label: "MIN",   val: manM, set: setManM, max: 59 },
              { label: "SEC",   val: manS, set: setManS, max: 59 },
            ].map((u) => (
              <div className="ixt-unit" key={u.label}>
                <button className="ixt-step" disabled={running} onClick={() => bumpUnit(u, 1)}>+</button>
                <div className="ixt-unit-val">{pad2(u.val)}</div>
                <button className="ixt-step" disabled={running} onClick={() => bumpUnit(u, -1)}>−</button>
                <div className="ixt-unit-label">{u.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="ixt-actions">
          <button className="ixt-btn ixt-back" onClick={onBack}>Back</button>
          {running || alarm ? (
            <button className="ixt-btn ixt-stop" onClick={stop}>{alarm ? "Stop Alarm" : "Stop"}</button>
          ) : (
            <button className="ixt-btn ixt-start" onClick={requestStart} disabled={belowMin}>Start</button>
          )}
        </div>
        <div className="ixt-note">
          {belowMin ? "30 second minimum." : "For fun only. Don't rely on the timer or bands for anything time-critical."}
        </div>
      </div>

      {/* Right: countdown (tap the whole panel to stop while alarming) */}
      <div
        className="ixt-panel ixt-clockpanel"
        onClick={alarm ? stop : undefined}
      >
        <div className="ixt-ringwrap">
          <svg className="ixt-ring-svg" viewBox="0 0 300 300">
            <defs>
              <linearGradient id="ixt-ring-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#007bff" />
                <stop offset="100%" stopColor="#64d2ff" />
              </linearGradient>
            </defs>
            <circle className="ixt-ring-track" cx="150" cy="150" r={RING_R} />
            <circle
              className="ixt-ring-bar"
              cx="150" cy="150" r={RING_R}
              stroke={ringStroke}
              strokeDasharray={RING_C}
              strokeDashoffset={RING_C * (1 - frac)}
            />
          </svg>
          <div
            className={`ixt-clock ${alarm ? "" : inLast10 ? "red" : inLast30 ? "amber" : ""}`}
            style={{ fontSize: clockSize }}
          >
            {clockStr}
          </div>
        </div>
        <div className="ixt-sub">
          {alarm ? "TIME'S UP — TAP TO STOP" : running ? "COUNTING DOWN" : "READY"}
        </div>
      </div>

      {pendingControl && hasHolder && holder?.holderDeviceId !== device?._id && (
        <div className="beacon-handoff-backdrop">
          <div className="beacon-handoff-modal">
            <h3>Take Control of Your Magic Beacon?</h3>
            <p>
              Your Wait Time {holder.holderDeviceType}: {holder.holderLabel} is
              currently controlling the beacon. The Timer needs control to
              light your bands. Switch control to this device?
            </p>
            <div className="beacon-handoff-buttons">
              <button className="beacon-handoff-no" onClick={cancelTakeControl}>
                No
              </button>
              <button className="beacon-handoff-yes" onClick={confirmTakeControl}>
                Yes, Switch
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractTimer;
